'use client';

import type { ReactNode } from 'react';
import { VStack } from '@terra/ui-styled-system';
import {
  SettingsEmptyState,
  SettingsPageShell,
  Skeleton,
  type SettingsNavItemConfig,
} from '@terra/ui-design-system';

type NetworkSettingsShellProps = {
  title: string;
  navItems: SettingsNavItemConfig[];
  activeNavItem: string;
  onNavItemChange: (value: string) => void;
  isLoading?: boolean;
  errorMessage?: string | null;
  emptyStateMessage?: string | null;
  children: ReactNode;
};

function NetworkSettingsShellBody({
  isLoading,
  errorMessage,
  emptyStateMessage,
  children,
}: Pick<
  NetworkSettingsShellProps,
  'isLoading' | 'errorMessage' | 'emptyStateMessage' | 'children'
>) {
  if (isLoading) {
    return (
      <VStack gap={4} alignItems="stretch">
        <Skeleton h="120px" w="full" />
        <Skeleton h="200px" w="full" />
      </VStack>
    );
  }

  const unavailableMessage = errorMessage ?? emptyStateMessage;

  if (unavailableMessage) {
    return <SettingsEmptyState>{unavailableMessage}</SettingsEmptyState>;
  }

  return children;
}

export function NetworkSettingsShell({
  title,
  navItems,
  activeNavItem,
  onNavItemChange,
  isLoading,
  errorMessage,
  emptyStateMessage,
  children,
}: NetworkSettingsShellProps) {
  return (
    <SettingsPageShell
      title={title}
      navItems={navItems}
      activeNavItem={activeNavItem}
      onNavItemChange={onNavItemChange}
    >
      <NetworkSettingsShellBody
        isLoading={isLoading}
        errorMessage={errorMessage}
        emptyStateMessage={emptyStateMessage}
      >
        {children}
      </NetworkSettingsShellBody>
    </SettingsPageShell>
  );
}
